import { get, trim } from "lodash";
import { FormEvent, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Icon } from "@shared/ui";

import Pencil from "../../icons/Pencil";
import { updateClientsConfig } from "../../lib/automatic";
import { genericClientLogoUrl } from "../../lib/constants";
import ButtonPrimary from "../ButtonPrimary";
import ClientLogoEditDialog from "../ClientLogoEditDialog";
import FieldLabel from "./FieldLabel";
import SettingsSection from "./SettingsSection";

const inputClassName =
  "w-full rounded border border-gray-300 p-2.5 text-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

const GeneralSection = ({
  id,
  data,
  defaultClientId,
  canEdit = false,
  onLogoUpdated,
  onDisplayNameUpdated,
}: {
  id?: string;
  data: Record<string, unknown>;
  defaultClientId?: string;
  canEdit?: boolean;
  onLogoUpdated?: (logoUrl: string) => void;
  onDisplayNameUpdated?: (displayName: string) => void;
}) => {
  const clientId = get(data, "id", defaultClientId) as string;
  const entity = get(data, "entity") as string | undefined;
  const initialDisplayName = get(data, "displayName") as string | undefined;
  const initialLogoUrl = get(data, "logoUrl") as string | undefined;

  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl as string | undefined);
  const [logoVersion, setLogoVersion] = useState(0);
  const [displayName, setDisplayName] = useState(initialDisplayName ?? "");
  const [isSavingDisplayName, setIsSavingDisplayName] = useState(false);

  useEffect(() => {
    setLogoUrl(initialLogoUrl as string | undefined);
  }, [initialLogoUrl]);

  useEffect(() => {
    setDisplayName(initialDisplayName ?? "");
  }, [initialDisplayName]);

  const baseLogoUrl = logoUrl ?? genericClientLogoUrl;
  const displayedLogoUrl =
    logoVersion > 0 && logoUrl
      ? `${baseLogoUrl}${baseLogoUrl.includes("?") ? "&" : "?"}v=${logoVersion}`
      : baseLogoUrl;

  const handleLogoSaved = (data: { logoUrl?: string }) => {
    if (data.logoUrl) {
      setLogoUrl(data.logoUrl);
      setLogoVersion(Date.now());
      onLogoUpdated?.(data.logoUrl);
    }
  };

  const handleSaveDisplayName = async (e: FormEvent) => {
    e.preventDefault();
    const updatedDisplayName = trim(displayName);
    setIsSavingDisplayName(true);
    try {
      const [, error] = await updateClientsConfig(
        clientId,
        undefined,
        updatedDisplayName
      );
      if (error) {
        toast.error(error.message || "Could not save display name");
        return;
      }

      setDisplayName(updatedDisplayName);
      onDisplayNameUpdated?.(updatedDisplayName);
      toast.success("Display name updated");
    } finally {
      setIsSavingDisplayName(false);
    }
  };

  return (
    <>
      <SettingsSection
        id={id}
        title="General"
        subtitle="Your organization's profile information."
      >
        <div className="divide-y divide-gray-200">
          <div className="flex items-center justify-between py-4 first:pt-0 group">
            <span className="text-sm font-medium text-gray-700">Logo</span>
            <div className="relative">
              <div
                className="h-10 w-10 rounded-full bg-white bg-cover bg-center bg-no-repeat border border-gray-200"
                style={{ backgroundImage: `url('${displayedLogoUrl}')` }}
              />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setLogoDialogOpen(true)}
                  className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-800 text-white shadow hover:bg-gray-700 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Update logo"
                >
                  <Icon icon={Pencil} height="h-2.5" width="w-2.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between py-4">
            <span className="text-sm font-medium text-gray-700">Client ID</span>
            <span className="text-sm text-gray-900 bg-gray-100 px-3 py-2 rounded-md font-mono">
              {clientId}
            </span>
          </div>

          <div className="flex items-center justify-between py-4">
            <span className="text-sm font-medium text-gray-700">Entity</span>
            <span className="text-sm text-gray-900">{entity ?? "—"}</span>
          </div>

          <div className="py-4 last:pb-0">
            {canEdit ? (
              <form
                onSubmit={handleSaveDisplayName}
                className="flex flex-col gap-y-6"
              >
                <div className="flex flex-col gap-y-2">
                  <FieldLabel htmlFor="display-name" required>
                    Display Name
                  </FieldLabel>
                  <input
                    id="display-name"
                    className={inputClassName}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={isSavingDisplayName}
                    required
                  />
                </div>

                <div className="flex justify-end">
                  <ButtonPrimary
                    text={isSavingDisplayName ? "Saving…" : "Save"}
                    type="submit"
                    disabled={isSavingDisplayName}
                    icon={undefined}
                    className=""
                  />
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Display Name
                </span>
                <span className="text-sm text-gray-900">
                  {displayName || "—"}
                </span>
              </div>
            )}
          </div>
        </div>
      </SettingsSection>

      <ClientLogoEditDialog
        isOpen={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        clientId={clientId}
        clientDisplayName={displayName}
        clientEntity={entity}
        currentLogoUrl={logoUrl}
        onSaved={handleLogoSaved}
      />
    </>
  );
};

export default GeneralSection;
